import {useGetBasicDataItems} from '@/modules/basic-data/api'
import {useGetInfiniteDrugsCategories} from '@/modules/basic-data/categories/api'
import {mapBasicDataItemsToSelectItems} from '@/modules/basic-data/functions'
import {Route} from '@/routes/configuration/drug-catalog/solutions'
import {FilterButton} from '@cypro/ui/components/ui/filter-button'
import {ResourcesFiltersContainer} from '@/shared/components/ui/resources-filters-container'
import {useAppForm} from '@cypro/ui/integrations/form'
import {Box, HStack, IconButton} from '@chakra-ui/react'
import {BasicDataResources} from '@cypro/engine/schema'
import {useStore} from '@tanstack/react-form'
import {useNavigate} from '@tanstack/react-router'
import {useTranslate} from '@tolgee/react'
import {LuCircleX} from 'react-icons/lu'

export function SolutionsFilters() {
  const {t} = useTranslate('common')

  const search = Route.useSearch()
  const navigate = useNavigate()

  const form = useAppForm({
    defaultValues: {
      search: search.filters?.search || '',
      categories: search.filters?.categories || [],
      types: search.filters?.types || [],
    },
    listeners: {
      onChange({formApi, fieldApi}) {
        // if triggered by the searchbox, prevent the navigation because we want to debouce the "name" filter
        // and we do it directly on the <form.AppField />
        if (fieldApi.name === 'search') return

        const {values} = formApi.state

        navigate({
          to: '/configuration/drug-catalog/solutions',
          search: prev => ({
            ...prev,
            filters: {
              categories: values.categories,
              types: values.types,
              search: values.search,
            },
          }),
        })
      },
    },
  })

  const values = useStore(form.store, state => state.values)
  const hasValues = Boolean(values?.search || values?.categories?.length || values?.types?.length)

  function onClearFilters() {
    form.setFieldValue('search', '')
    form.setFieldValue('types', [])
    form.setFieldValue('categories', [])
  }

  const infiniteCategories = useGetInfiniteDrugsCategories()
  const {items: solutionTypes} = useGetBasicDataItems({resource: BasicDataResources.solutionTypes})

  return (
    <ResourcesFiltersContainer>
      <form.AppForm>
        <HStack>
          <form.AppField name="categories">
            {field => (
              <field.InfiniteCombobox
                label={t('categories')}
                hideLabel
                multiple
                {...infiniteCategories}
              >
                <FilterButton isApplied={Boolean(form.state.values.categories?.length)}>{t('categories')}</FilterButton>
              </field.InfiniteCombobox>
            )}
          </form.AppField>

          <form.AppField name="types">
            {field => (
              <field.Select
                items={mapBasicDataItemsToSelectItems(solutionTypes, {includeCode: false})}
                label={t('types')}
                hideLabel
                multiple
              >
                <FilterButton isApplied={Boolean(form.state.values.types?.length)}>{t('types')}</FilterButton>
              </field.Select>
            )}
          </form.AppField>

          {hasValues && (
            <IconButton variant="ghost" title={t('clear_filters')} onClick={onClearFilters}>
              <LuCircleX />
            </IconButton>
          )}
        </HStack>

        <Box>
          <form.AppField
            name="search"
            listeners={{
              onChange: ({fieldApi}) =>
                navigate({
                  to: '/configuration/drug-catalog/solutions',
                  search: prev => ({...prev, filters: {...prev.filters, search: fieldApi.state.value}}),
                }),
              onChangeDebounceMs: 300,
            }}
          >
            {field => <field.Searchbox />}
          </form.AppField>
        </Box>
      </form.AppForm>
    </ResourcesFiltersContainer>
  )
}
